import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PortalSetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/client-portal/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: form.password }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Couldn't set password — the link may have expired");
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#142129] flex items-center justify-center p-4" data-testid="portal-set-password">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-t-2xl px-8 py-6 flex justify-center">
          <img src="/site-assets/logo.png" alt="Wales Event Medical Services" className="h-16 w-auto" />
        </div>
        <div className="bg-white/95 rounded-b-2xl px-8 pb-8 pt-2 border-t border-slate-100">
          {done ? (
            <div className="text-center py-6" data-testid="set-password-success">
              <KeyRound className="h-10 w-10 text-[#1e7d8c] mx-auto mb-3" />
              <h1 className="text-xl font-bold text-[#1c3d49]">Password set</h1>
              <p className="text-sm text-slate-500 mt-2">You can now log in to your client portal.</p>
              <button onClick={() => navigate("/portal/login")} data-testid="set-password-login"
                className="mt-5 h-12 px-6 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] text-white text-sm font-semibold transition-colors">
                Go to login
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-[#1c3d49]">Set up your password</h1>
              <p className="text-sm text-slate-500 mt-1">Choose a password for your client portal — at least 8 characters.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <input type="password" required value={form.password} data-testid="set-password-new"
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="New password"
                  className="w-full h-12 rounded-lg border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e7d8c]" />
                <input type="password" required value={form.confirm} data-testid="set-password-confirm"
                  onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))}
                  placeholder="Confirm password"
                  className="w-full h-12 rounded-lg border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e7d8c]" />
                {error && <p className="text-sm text-[#D5281B] font-medium" data-testid="set-password-error">{error}</p>}
                <button type="submit" disabled={loading} data-testid="set-password-submit"
                  className="w-full h-12 rounded-lg bg-[#1e7d8c] hover:bg-[#176472] disabled:opacity-50 transition-colors text-white text-sm font-semibold">
                  {loading ? "Saving…" : "Set password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
